import { create } from "zustand";
import { SECTIONS } from "../../constants/sections.jsx";
import { DEFAULT_ROUTE_ID } from "../../constants/routes.js";
import { pushAppUrl } from "../routing/urlHistory.js";
import { isSyncingFromUrl } from "../routing/urlRouting.js";
import { useAccountsStore } from "./accountsStore.js";

const MAIN_ROUTE_LOAD_MS = 420;
let routeTimer = null;

export const useNavigationStore = create((set, get) => ({
  active: DEFAULT_ROUTE_ID,
  sbState: "open",
  openSec: Object.fromEntries(SECTIONS.map((s) => [s.id, s.id === "accounts"])),
  hoveredSec: null,
  mainRouteLoading: false,
  pendingRoute: null,

  setActiveFromUrl: (id) => {
    const parent = SECTIONS.find((s) => s.children.some((ch) => ch.id === id));
    if (parent) {
      set((s) => ({ openSec: { ...s.openSec, [parent.id]: true } }));
    }
    if (routeTimer) clearTimeout(routeTimer);
    set({ active: id, mainRouteLoading: false, pendingRoute: null });
  },

  navigateTo: (id, { fromUrl = false } = {}) => {
    if (!fromUrl && id === get().active) {
      if (id === "acc-ov" && useAccountsStore.getState().detailAccountId) {
        useAccountsStore.getState().closeAccountDetail();
      }
      return;
    }

    const parent = SECTIONS.find((s) => s.children.some((ch) => ch.id === id));
    if (parent) {
      set((s) => ({ openSec: { ...s.openSec, [parent.id]: true } }));
    }

    if (fromUrl) {
      if (routeTimer) clearTimeout(routeTimer);
      set({ active: id, mainRouteLoading: false, pendingRoute: null });
      return;
    }

    if (id !== "acc-ov" && useAccountsStore.getState().detailAccountId) {
      useAccountsStore.setState({ detailAccountId: null, detailTab: "info" });
    }

    if (routeTimer) clearTimeout(routeTimer);
    set({ pendingRoute: id, mainRouteLoading: true });
    if (!isSyncingFromUrl()) {
      const accounts = useAccountsStore.getState();
      pushAppUrl({
        routeId: id,
        accountId: id === "acc-ov" ? accounts.detailAccountId : null,
        tab: accounts.detailTab,
      });
    }
    routeTimer = setTimeout(() => {
      set({ active: id, mainRouteLoading: false, pendingRoute: null });
      routeTimer = null;
    }, MAIN_ROUTE_LOAD_MS);
  },

  setSbState: (sbState) => set({ sbState }),
  toggleOpenSec: (secId) => set((s) => ({ openSec: { ...s.openSec, [secId]: !s.openSec[secId] } })),
  setHoveredSec: (hoveredSec) => set({ hoveredSec }),
}));

export const useActiveRoute = () => useNavigationStore((s) => s.active);
export const useNavigateTo = () => useNavigationStore((s) => s.navigateTo);
export const useSbState = () => useNavigationStore((s) => s.sbState);
export const useSetSbState = () => useNavigationStore((s) => s.setSbState);
export const useMainRouteLoading = () => useNavigationStore((s) => s.mainRouteLoading);
export const usePendingRoute = () => useNavigationStore((s) => s.pendingRoute);
export const useOpenSec = () => useNavigationStore((s) => s.openSec);
export const useToggleOpenSec = () => useNavigationStore((s) => s.toggleOpenSec);
export const useHoveredSec = () => useNavigationStore((s) => s.hoveredSec);
export const useSetHoveredSec = () => useNavigationStore((s) => s.setHoveredSec);
